import { useQuery } from '@tanstack/react-query';
import { Building2Icon, ChevronRightIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SubAsset } from '@/common/sub-asset';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { buildSubAssetPath, getTreeView } from '@/lib/utils/asset';
import { getAssetOptions } from '@/queries/assets/get-asset';
import { getListSubAssetsOptions } from '@/queries/sub-assets/list-sub-assets';

import { SubAssetTreeSelector } from '../../sub-asset/sub-asset-selector/SubAssetTreeSelector';

/**
 * The asset/sub-asset tree a 3D data upload is filed under, plus the selected
 * node. Owned by Add3DDataDialog rather than by either panel, so the selection
 * survives a switch between the upload and Cesium ION tabs.
 */
export type AssetStructureState = {
	selectedId: string;
	setSelectedId: (id: string) => void;
	subAssets: SubAsset[];
	breadcrumb: (id: string) => string;
};

/**
 * What Add3DDataDialog hands each of its panels: the target the upload is filed
 * against, the shared fields, and a way to close the dialog once it succeeds.
 */
export type Add3DDataPanelProps = {
	onAdd?: () => void;
	onClose: () => void;
	assetId?: string;
	inspectionId?: string;
	damageId?: string;
	name: string;
	setName: (name: string) => void;
	assetStructure: AssetStructureState;
};

/** Loads the asset's sub-asset tree, rooted at the asset itself. */
export function useAssetStructure(
	assetId: string | undefined,
	selectedId: string,
	setSelectedId: (id: string) => void,
): AssetStructureState {
	const { data: subAssetsResponse } = useQuery(getListSubAssetsOptions(assetId || '', { limit: 300, offset: 0 }));
	const { data: asset } = useQuery(getAssetOptions(assetId || ''));

	const subAssets = useMemo((): SubAsset[] => {
		if (!subAssetsResponse?.subAssets || !asset?.id) return [];
		const children = getTreeView(subAssetsResponse?.subAssets);
		return [
			{
				id: asset?.id as string,
				name: asset?.name || '',
				parentSubAssetId: null,
				children,
			},
		];
	}, [subAssetsResponse?.subAssets, asset?.id, asset?.name]);

	return useMemo(
		() => ({
			selectedId,
			setSelectedId,
			subAssets,
			breadcrumb: (id: string) =>
				buildSubAssetPath(subAssets, id)
					.map((s) => s.name || '')
					.join(' > '),
		}),
		[selectedId, setSelectedId, subAssets],
	);
}

/** Display name for the data upload. Shared by both 3D data sources. */
export function NameField({
	value,
	onChange,
	label,
	placeholder,
}: {
	value: string;
	onChange: (value: string) => void;
	label?: string;
	placeholder?: string;
}) {
	const { t } = useTranslation();
	const resolvedLabel = label ?? t('files.upload.nameLabel');
	const resolvedPlaceholder = placeholder ?? t('files.upload.namePlaceholder');
	return (
		<div>
			<Label className="text-sm font-medium mb-2 block">
				{resolvedLabel} <span className="text-red-500">*</span>
			</Label>
			<Input
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={resolvedPlaceholder}
				className="bg-white"
			/>
		</div>
	);
}

/** Asset/sub-asset picker. Shared by both 3D data sources. */
export function AssetStructureField({ state, required = false }: { state: AssetStructureState; required?: boolean }) {
	const { t } = useTranslation();
	const [isTreeOpen, setIsTreeOpen] = useState(false);

	return (
		<div>
			<Label className="text-sm font-medium mb-2 block">
				{t('files.upload.assetStructureLabel')} {required && <span className="text-red-500">*</span>}
			</Label>
			<div className="space-y-2">
				<Button
					type="button"
					variant="outline"
					className="bg-input rounded p-3 py-5 cursor-pointer transition-colors w-full justify-start"
					onClick={() => setIsTreeOpen(!isTreeOpen)}
				>
					<div className="flex items-center justify-between w-full text-foreground">
						<div className="flex items-center gap-2">
							<Building2Icon className="size-4" />
							<span className="text-sm">
								{state.selectedId ? state.breadcrumb(state.selectedId) : t('files.upload.selectAssetPlaceholder')}
							</span>
						</div>
						<ChevronRightIcon className={cn('size-4 transition-transform', isTreeOpen && 'rotate-90')} />
					</div>
				</Button>

				{isTreeOpen && (
					<SubAssetTreeSelector
						selectedId={state.selectedId}
						onSelect={(id: string) => {
							state.setSelectedId(id);
							setIsTreeOpen(false);
						}}
						subAssets={state.subAssets}
					/>
				)}
			</div>
		</div>
	);
}
